/* eslint-disable max-lines */
import { isDefined } from "@clipboard-health/util-ts";
import {
  DeploymentEnvironmentName,
  environmentConfig,
  isDevelopmentNodeEnvironment,
} from "@src/appV2/environment";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { isCapacitorPlatform } from "@src/appV2/lib";
import { logError, logEvent } from "@src/appV2/lib/analytics";
import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import { useTimers } from "@src/appV2/lib/utils/useTimers";
import { UserLocationState } from "@src/appV2/Shifts/Shift/ShiftState/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import constate from "constate";
import { addMinutes, differenceInMilliseconds, isAfter } from "date-fns";
import { HyperTrack, type Subscription } from "hypertrack-sdk-ionic-capacitor";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useLazyIsInsideGeofence } from "../hooks/useIsInsideGeofence";
import { orderHandleForShift } from "../providers/hypertrackLocationProvider";
import { type GeoLocation } from "../types";

const GEOFENCE_POLLING_INTERVAL_MS = 10_000;
const GEOFENCE_STATE_CHANGE_THRESHOLD_MINUTES = 5;
// Keep query data in the React Query cache for 5 minutes after the provider
// unmounts. When the worker navigates away and back to the same shift within
// that window the provider can read from the cache in its useState initializer,
// restoring the last-known inside/outside state immediately rather than
// defaulting to initialLocation and flashing the wrong UI for one poll cycle.
const GEOFENCE_QUERY_CACHE_TIME_MS = 5 * 60 * 1000;

// Generous thresholds guard against runaway timers (e.g. a HyperTrack
// subscription that never delivers an order). When a timer exceeds its
// threshold, `useTimers.stop` emits TIMER_EXPIRED and returns undefined so
// the duration is omitted from the corresponding event rather than reported
// as a misleading large value.
const GEOFENCE_POLLING_ABANDONMENT_THRESHOLD_MS = 60_000;
const GEOFENCE_SUBSCRIPTION_ABANDONMENT_THRESHOLD_MS = 5 * 60_000;

function pollingTimerKey(shiftId: string): string {
  return `geofence-poll:${shiftId}`;
}

function subscriptionTimerKey(shiftId: string): string {
  return `geofence-subscription:${shiftId}`;
}

function isDevelopmentEnvironment(): boolean {
  return (
    isDevelopmentNodeEnvironment() ||
    environmentConfig.REACT_APP_ENVIRONMENT_NAME === DeploymentEnvironmentName.DEVELOPMENT ||
    environmentConfig.REACT_APP_ENVIRONMENT_NAME === DeploymentEnvironmentName.LOCAL ||
    environmentConfig.REACT_APP_ENVIRONMENT_NAME === DeploymentEnvironmentName.STAGING ||
    environmentConfig.REACT_APP_ENVIRONMENT_NAME === DeploymentEnvironmentName.PROD_SHADOW
  );
}

// Development helper for web testing
const WEB_GEOFENCE_DEBUG_KEY = "cbh-web-geofence-debug";
function getWebGeofenceOverride(): boolean | undefined {
  if (typeof globalThis === "undefined" || isCapacitorPlatform() || !isDevelopmentEnvironment()) {
    return undefined;
  }

  const stored = localStorage.getItem(WEB_GEOFENCE_DEBUG_KEY);
  return stored ? JSON.parse(stored) : undefined;
}

function setWebGeofenceOverride(inside: boolean | undefined): void {
  if (typeof globalThis === "undefined" || isCapacitorPlatform() || !isDevelopmentEnvironment()) {
    return;
  }

  if (inside === undefined) {
    localStorage.removeItem(WEB_GEOFENCE_DEBUG_KEY);
  } else {
    localStorage.setItem(WEB_GEOFENCE_DEBUG_KEY, JSON.stringify(inside));
  }
}

/**
 * Expose debug functions globally for web testing (development mode only)
 *
 * These controls only affect the `useGeofence` hook behavior and allow overriding
 * geofence detection for testing clock-in/out workflows without physical location changes.
 *
 * Usage in browser console during development:
 * - `window.cbhGeofenceDebug.setInside(true)` - Force geofence status to "inside"
 * - `window.cbhGeofenceDebug.setOutside()` - Force geofence status to "outside"
 * - `window.cbhGeofenceDebug.clearOverride()` - Clear override and resume normal detection
 * - `window.cbhGeofenceDebug.getOverride()` - Check current override status
 *
 * Requirements:
 * - Only available in development mode (NODE_ENV=development or API environment=development/local)
 * - Only available on web platforms (not native apps)
 * - Only affects components using the `useGeofence` hook
 * - Overrides persist across page refreshes until cleared
 */
if (typeof globalThis !== "undefined" && !isCapacitorPlatform() && isDevelopmentEnvironment()) {
  (globalThis as any).cbhGeofenceDebug = {
    setInside: (inside: boolean) => {
      setWebGeofenceOverride(inside);
    },
    setOutside: () => {
      setWebGeofenceOverride(false);
    },
    clearOverride: () => {
      setWebGeofenceOverride(undefined);
    },
    getOverride: getWebGeofenceOverride,
  };
}

export type GeofenceDetectionMethod =
  | "hypertrack_subscription"
  | "fallback_polling"
  | "test_account_override";

export interface UseGeofenceProps {
  shiftId: string;
  workerId: string;
  workplaceId: string | undefined;
  initialLocation: UserLocationState | undefined;
  fallbackGeofenceLocation: GeoLocation | undefined;
  enableGeofenceTracking?: boolean;
}

function useGeofence(props: UseGeofenceProps) {
  const {
    shiftId,
    workerId,
    workplaceId,
    initialLocation = UserLocationState.OUTSIDE_GEOFENCE,
    fallbackGeofenceLocation,
    enableGeofenceTracking = false,
  } = props;

  const worker = useDefinedWorker();
  const isSupportPairingEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_SUPPORT_PAIRING, {
    defaultValue: false,
  });

  const isHyperTrackEnabled = useCbhFlag(CbhFeatureFlag.HYPERTRACK_CLOCK_IN_OUT, {
    defaultValue: false,
  });

  const isNative = isCapacitorPlatform();

  // Test account override: always inside when test account + support pairing enabled
  const isTestAccountOverride = worker.isTestAccount === true && isSupportPairingEnabled;

  // Determine if we can subscribe to HyperTrack based on current conditions
  const canSubscribeToHyperTrack = useMemo(() => {
    return isNative && isHyperTrackEnabled && enableGeofenceTracking;
  }, [isNative, isHyperTrackEnabled, enableGeofenceTracking]);

  const queryClient = useQueryClient();
  const queryKey = ["location/geofence", { shiftId, workerId }] as const;

  const [hyperTrackFailed, setHyperTrackFailed] = useState(false);
  const [inside, setInside] = useState(() => {
    if (isTestAccountOverride) {
      return true;
    }
    // Restore from the React Query cache when available (e.g. after navigating
    // away and back within GEOFENCE_QUERY_CACHE_TIME_MS) so the provider
    // initializes to the last-known state without waiting for the first poll.
    const cached = queryClient.getQueryData<{
      data?: { isInsideGeofence?: boolean } | null;
    }>(queryKey);
    if (isDefined(cached?.data?.isInsideGeofence)) {
      return cached.data.isInsideGeofence;
    }
    return initialLocation === UserLocationState.INSIDE_GEOFENCE;
  });
  const lastInsideChangeTimestampRef = useRef(new Date());

  const lastGeofenceStateChangeTimestampRef = useRef<Date | null>(null);
  const [lastGeofenceChangeAt, setLastGeofenceChangeAt] = useState<Date | undefined>(undefined);

  // Tracks user-initiated refetches (Try Again) so the LoadingButton can
  // reflect the actual wait. Distinct from React Query's `isFetching`, which
  // also fires for background `refetchInterval` polls and would falsely flash
  // the button while the modal is open.
  const [isRefetchingGeofence, setIsRefetchingGeofence] = useState(false);

  // Destructure to stable callbacks so effects that depend on them don't
  // re-run on every render (useTimers returns a new object each call, but
  // start/stop are useCallback-wrapped with [] deps and never change identity).
  const { start: startTimer, stop: stopTimer } = useTimers();

  function computeDetectionMethod(): GeofenceDetectionMethod {
    if (isTestAccountOverride) {
      return "test_account_override";
    }
    if (canSubscribeToHyperTrack && !hyperTrackFailed) {
      return "hypertrack_subscription";
    }
    return "fallback_polling";
  }
  const detectionMethod = computeDetectionMethod();

  const getIsInsideGeofence = useLazyIsInsideGeofence({
    requestUniqueId: {
      workerId,
      shiftId,
    },
    action: "calculate-driving-distance",
    fallbackGeofenceLocation,
  });

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const timerKey = pollingTimerKey(shiftId);
      // Guard against a cancelled in-flight poll (e.g. manual refetch with cancelRefetch:true)
      // overwriting its timer silently. stopTimer is a no-op if no timer is running.
      stopTimer(timerKey);
      startTimer(timerKey, { abandonmentThresholdMs: GEOFENCE_POLLING_ABANDONMENT_THRESHOLD_MS });

      const startEvent = {
        shiftId,
        workerId,
        workplaceId,
        method: "fallback_polling",
      };
      logEvent(APP_V2_USER_EVENTS.GEOFENCE_POLLING_STARTED, startEvent);

      try {
        const result = await getIsInsideGeofence();

        const duration = stopTimer(timerKey);
        const completedEvent = {
          shiftId,
          workerId,
          workplaceId,
          method: "fallback_polling",
          isInsideGeofence: result?.data?.isInsideGeofence,
          success: true,
          canSubscribeToHyperTrack,
          hyperTrackFailed,
          isTestAccountOverride,
          ...(isDefined(duration) && {
            durationMs: duration.activeDurationMs,
            totalDurationMs: duration.totalDurationMs,
          }),
        };
        logEvent(APP_V2_USER_EVENTS.GEOFENCE_POLLING_COMPLETED, completedEvent);

        return result;
      } catch (error) {
        // Discard the in-flight timer so the next poll starts cleanly. If the
        // timer already expired, the TIMER_EXPIRED event covers that case.
        stopTimer(timerKey);
        const errorEvent = {
          error,
          metadata: {
            shiftId,
            workerId,
            workplaceId,
            method: "fallback_polling",
          },
        };
        logError(APP_V2_APP_EVENTS.GEOFENCE_POLLING_FAILED, errorEvent);
        throw error;
      }
    },
    enabled: !isTestAccountOverride && (!canSubscribeToHyperTrack || hyperTrackFailed), // Skip polling when test account override is active
    refetchInterval: GEOFENCE_POLLING_INTERVAL_MS,
    staleTime: GEOFENCE_POLLING_INTERVAL_MS,
    cacheTime: GEOFENCE_QUERY_CACHE_TIME_MS,
    refetchOnMount: "always",
  });

  /**
   * In some cases when worker devices have poor GPS reception,
   * We are getting frequent changes to geofence status. These can change inside state
   * to be outside and vice versa many times each minute. This manifests to workers
   * as showing/hiding of the clock in/out buttons and effectively prevents
   * then from using the time clock.
   *
   * We are implementing a 5 minute threshold which ignores changes from inside to outside
   * to prevent issues workers are facing.
   *
   * The assumption here is that a geofence check can't be falsely set to inside if the worker
   * is far from the workplace. If geofence status is set to inside, that means the worker is
   * either inside of the workplace or very near it.
   */
  const changeInsideState = useCallback(
    (newInside: boolean, method: string, checkDurationMs?: number) => {
      const changeToOutside = inside && !newInside;
      const isInThreshold =
        isDefined(lastInsideChangeTimestampRef.current) &&
        isAfter(
          addMinutes(lastInsideChangeTimestampRef.current, GEOFENCE_STATE_CHANGE_THRESHOLD_MINUTES),
          new Date()
        );

      if (changeToOutside && isInThreshold) {
        logEvent(APP_V2_USER_EVENTS.GEOFENCE_STATUS_CHANGED_IGNORED, {
          shiftId,
          workerId,
          workplaceId,
          method,
          previousInside: inside,
          newInside,
          canSubscribeToHyperTrack,
          hyperTrackFailed,
          isTestAccountOverride,
          lastChangeTimestamp: lastInsideChangeTimestampRef.current,
        });
        return;
      }

      if (newInside !== inside) {
        const now = new Date();
        const timeSinceLastChangeMs = isDefined(lastGeofenceStateChangeTimestampRef.current)
          ? differenceInMilliseconds(now, lastGeofenceStateChangeTimestampRef.current)
          : undefined;

        lastGeofenceStateChangeTimestampRef.current = now;
        setLastGeofenceChangeAt(now);

        logEvent(APP_V2_USER_EVENTS.GEOFENCE_STATUS_CHANGED, {
          shiftId,
          workerId,
          workplaceId,
          method,
          previousInside: inside,
          newInside,
          queryDataExists: Boolean(query.data),
          initialLocationFallback: initialLocation === UserLocationState.INSIDE_GEOFENCE,
          // Time since the previous status change (Phase 1 metric).
          ...(isDefined(timeSinceLastChangeMs) && { timeSinceLastChangeMs }),
          // Time the geofence check itself took, sourced from `useTimers` (TEM-2002).
          ...(isDefined(checkDurationMs) && { checkDurationMs }),
        });
      }

      if (newInside) {
        lastInsideChangeTimestampRef.current = new Date();
      }
      setInside(newInside);
    },
    [
      inside,
      shiftId,
      workerId,
      workplaceId,
      canSubscribeToHyperTrack,
      hyperTrackFailed,
      isTestAccountOverride,
      query.data,
      initialLocation,
    ]
  );

  useEffect(() => {
    if (isTestAccountOverride || !canSubscribeToHyperTrack || hyperTrackFailed) {
      return;
    }

    let ordersUnsubscribe: Subscription | undefined;

    async function setupSubscriptions() {
      try {
        const setupStartEvent = {
          shiftId,
          workerId,
          workplaceId,
        };
        logEvent(APP_V2_USER_EVENTS.HYPERTRACK_SUBSCRIPTION_SETUP_STARTED, setupStartEvent);

        HyperTrack.setWorkerHandle(workerId);

        const subscriptionKey = subscriptionTimerKey(shiftId);
        // Start timing now; each `subscribeToOrders` callback stops + restarts
        // the timer so `durationMs` measures the gap between order updates.
        startTimer(subscriptionKey, {
          abandonmentThresholdMs: GEOFENCE_SUBSCRIPTION_ABANDONMENT_THRESHOLD_MS,
        });

        ordersUnsubscribe = HyperTrack.subscribeToOrders(async (orders) => {
          const orderHandle = orderHandleForShift({ shiftId, workerId });
          const order = orders.get(orderHandle);
          const duration = stopTimer(subscriptionKey);
          startTimer(subscriptionKey, {
            abandonmentThresholdMs: GEOFENCE_SUBSCRIPTION_ABANDONMENT_THRESHOLD_MS,
          });

          const orderReceivedEvent = {
            shiftId,
            workerId,
            workplaceId,
            orderHandle,
            hasOrder: Boolean(order),
            ...(isDefined(duration) && {
              durationMs: duration.activeDurationMs,
              totalDurationMs: duration.totalDurationMs,
            }),
          };
          logEvent(APP_V2_USER_EVENTS.HYPERTRACK_ORDER_RECEIVED, orderReceivedEvent);

          const isInsideGeofence = await order?.isInsideGeofence();

          if (isInsideGeofence?.type !== "success") {
            const checkFailedEvent = {
              shiftId,
              workerId,
              workplaceId,
              resultType: isInsideGeofence?.type,
            };
            logEvent(APP_V2_USER_EVENTS.HYPERTRACK_GEOFENCE_CHECK_FAILED, checkFailedEvent);
            // trigger GPS fallback
            setHyperTrackFailed(true);
            return;
          }

          changeInsideState(
            isInsideGeofence.value,
            "hypertrack_subscription",
            duration?.activeDurationMs
          );
        });

        const setupCompletedEvent = {
          shiftId,
          workerId,
          workplaceId,
        };
        logEvent(APP_V2_USER_EVENTS.HYPERTRACK_SUBSCRIPTION_SETUP_COMPLETED, setupCompletedEvent);
      } catch (error) {
        stopTimer(subscriptionTimerKey(shiftId));
        // trigger GPS fallback
        setHyperTrackFailed(true);
        const setupFailedEvent = {
          error,
          metadata: {
            shiftId,
            workerId,
            workplaceId,
          },
        };
        logError(APP_V2_APP_EVENTS.HYPERTRACK_SUBSCRIPTION_SETUP_FAILED, setupFailedEvent);
      }
    }

    void setupSubscriptions();

    return function () {
      void ordersUnsubscribe?.remove();
      // Discard any in-flight timer; if the timer already expired, the
      // TIMER_EXPIRED event covers that case. Without this, an unmount mid-
      // wait would leak the timer entry until the next setup overwrote it.
      stopTimer(subscriptionTimerKey(shiftId));
    };
  }, [
    canSubscribeToHyperTrack,
    hyperTrackFailed,
    workerId,
    workplaceId,
    shiftId,
    inside,
    isTestAccountOverride,
    changeInsideState,
    startTimer,
    stopTimer,
  ]);

  // Use fallback data when not using subscriptions
  useEffect(() => {
    if (isTestAccountOverride || (canSubscribeToHyperTrack && !hyperTrackFailed)) {
      return;
    }

    const webOverride = getWebGeofenceOverride();

    const fallbackInside =
      webOverride ??
      ((query.data?.data?.isInsideGeofence ?? false) ||
        initialLocation === UserLocationState.INSIDE_GEOFENCE);

    changeInsideState(
      fallbackInside,
      isDefined(webOverride) ? "web_debug_override" : "fallback_polling"
    );
  }, [
    canSubscribeToHyperTrack,
    hyperTrackFailed,
    query.data,
    query.dataUpdatedAt,
    initialLocation,
    inside,
    shiftId,
    workerId,
    isTestAccountOverride,
    changeInsideState,
  ]);

  // Refetch function to manually trigger a geofence check (used by "Try Again").
  const refetchGeofence = useCallback(async () => {
    if (isTestAccountOverride) {
      return { inside: true };
    }

    setIsRefetchingGeofence(true);
    const startedAt = new Date();

    if (!canSubscribeToHyperTrack || hyperTrackFailed) {
      // Polling path: route through React Query so the cache and interval stay
      // in sync.
      try {
        const result = await query.refetch();
        const refetchDurationMs = differenceInMilliseconds(new Date(), startedAt);
        const newInside = result.data?.data?.isInsideGeofence ?? false;
        const error = result.error ?? (query.data?.data?.error as Error | undefined);
        return { inside: newInside, error, refetchDurationMs };
      } catch (error: unknown) {
        const refetchDurationMs = differenceInMilliseconds(new Date(), startedAt);
        return { inside: false, error: error as Error, refetchDurationMs };
      } finally {
        setIsRefetchingGeofence(false);
      }
    }

    // HyperTrack subscription path: the subscription is push-only so we do a
    // one-shot fallback GPS call to give the worker a real check with a loader.
    // The subscription continues in the background and will keep updating state
    // on the next order event. We also call changeInsideState so the provider's
    // local state is consistent before onClockIn() re-reads it.
    try {
      const result = await getIsInsideGeofence();
      const refetchDurationMs = differenceInMilliseconds(new Date(), startedAt);
      const newInside = result?.data?.isInsideGeofence ?? false;
      changeInsideState(newInside, "fallback_polling");
      return { inside: newInside, refetchDurationMs };
    } catch (error: unknown) {
      const refetchDurationMs = differenceInMilliseconds(new Date(), startedAt);
      return { inside, error: error as Error, refetchDurationMs };
    } finally {
      setIsRefetchingGeofence(false);
    }
  }, [
    isTestAccountOverride,
    canSubscribeToHyperTrack,
    hyperTrackFailed,
    query,
    inside,
    getIsInsideGeofence,
    changeInsideState,
  ]);

  // Provider lifecycle telemetry — fires once per provider instance so we can
  // measure how often the provider remounts across navigation (TEM-1972).
  useEffect(
    () => {
      logEvent(APP_V2_USER_EVENTS.GEOFENCE_PROVIDER_MOUNTED, {
        shiftId,
        workerId,
        workplaceId,
        initialLocation,
        enableGeofenceTracking,
        canSubscribeToHyperTrack,
        isTestAccountOverride,
      });

      return function () {
        logEvent(APP_V2_USER_EVENTS.GEOFENCE_PROVIDER_UNMOUNTED, {
          shiftId,
          workerId,
          workplaceId,
        });
      };
    },
    // Intentionally fires once per mount — these props are stable per provider instance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Use the error from the query or the data if it exists
  const error = (query.error ?? query.data?.data?.error) as Error | undefined;
  return {
    inside,
    refetchGeofence,
    isRefetchingGeofence,
    error,
    detectionMethod,
    lastGeofenceChangeAt,
  };
}

export const [GeofenceProvider, useGeofenceContext] = constate(useGeofence);
/* eslint-enable max-lines */
